/* Revenue Details, Rate-Lock Token Bookings & Stores Admin */
import React, { useEffect, useState } from "react";
import { hasRole } from "../../lib/roles";
import { api } from "../../lib/api";
import { fmtINR } from "../../lib/constants";
import { toast } from "sonner";
import { useAuth } from "../../lib/auth";
import { Plus, X, Pencil, Trash2, BadgeIndianRupee } from "lucide-react";
import DateInput from "../../components/DateInput";
import ImageUploader from "../../components/ImageUploader";

// ============ REVENUE DETAILS (dashboard → Revenue click) ============
export function RevenueDetailsPage() {
  const [summary, setSummary] = useState(null);
  const [orders, setOrders] = useState([]);
  const [flt, setFlt] = useState({ from: "", to: "", ptype: "", store: "" });
  useEffect(() => {
    api.get("/api/crm/dashboard/summary").then((r) => setSummary(r.data)).catch(() => {});
    api.get("/api/crm/orders").then((r) => setOrders((r.data || []).filter((o) => o.payment_status === "paid")));
  }, []);

  const payType = (o) => {
    const pid = o.razorpay_payment_id || "";
    if ((o.notes || "").includes("COUNTER") ) return "Counter";
    if (pid.startsWith("pay_mock") || pid.startsWith("order_mock")) return "Online (Test)";
    return pid ? "Razorpay Online" : "Online";
  };

  const filtered = orders.filter((o) => {
    const d = (o.paid_at || o.created_at || "").slice(0, 10);
    if (flt.from && d < flt.from) return false;
    if (flt.to && d > flt.to) return false;
    if (flt.ptype && payType(o) !== flt.ptype) return false;
    if (flt.store && (o.store || "") !== flt.store) return false;
    return true;
  });
  const stores = [...new Set(orders.map((o) => o.store).filter(Boolean))];
  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="eyebrow">Money</div>
        <h1 className="font-display text-4xl text-maroon">Revenue Details</h1>
        <p className="text-xs text-ash mt-1">Every paid order that makes up the revenue figures.</p>
      </div>
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            ["Orders Today", summary.orders_today],
            ["Revenue (7d)", fmtINR(summary.revenue_week)],
            ["Revenue (30d)", fmtINR(summary.revenue_month)],
            ["Revenue (Total)", fmtINR(summary.revenue_total)],
          ].map(([l, v]) => (
            <div key={l} className="bg-white border border-line p-5">
              <div className="flex items-center justify-between">
                <div className="eyebrow text-[9px]">{l}</div>
                <BadgeIndianRupee size={14} className="text-gold"/>
              </div>
              <div className="font-display text-lg sm:text-2xl text-maroon mt-2 break-words leading-tight">{v}</div>
            </div>
          ))}
        </div>
      )}
      {/* Filters: date range · payment type · store */}
      <div className="flex flex-wrap items-end gap-3 mb-4" data-testid="revenue-filters">
        <label className="text-xs"><span className="eyebrow text-[9px] block mb-1">From date</span>
          <DateInput yearsBack={3} yearsAhead={1} value={flt.from} onChange={(v) => setFlt({...flt, from: v})} data-testid="rev-from"/></label>
        <label className="text-xs"><span className="eyebrow text-[9px] block mb-1">To date</span>
          <DateInput yearsBack={3} yearsAhead={1} value={flt.to} onChange={(v) => setFlt({...flt, to: v})} data-testid="rev-to"/></label>
        <label className="text-xs"><span className="eyebrow text-[9px] block mb-1">Payment type</span>
          <select className="border border-line px-2 py-1.5 bg-white" value={flt.ptype} onChange={(e) => setFlt({...flt, ptype: e.target.value})} data-testid="rev-ptype">
            <option value="">All</option><option>Counter</option><option>Razorpay Online</option><option>Online (Test)</option>
          </select></label>
        {stores.length > 0 && (
          <label className="text-xs"><span className="eyebrow text-[9px] block mb-1">Store</span>
            <select className="border border-line px-2 py-1.5 bg-white" value={flt.store} onChange={(e) => setFlt({...flt, store: e.target.value})} data-testid="rev-store">
              <option value="">All</option>{stores.map((s) => <option key={s}>{s}</option>)}
            </select></label>
        )}
        {(flt.from || flt.to || flt.ptype || flt.store) && <button onClick={() => setFlt({ from: "", to: "", ptype: "", store: "" })} className="text-xs text-maroon underline">Clear</button>}
        <div className="text-xs text-ash ml-auto">{filtered.length} order(s) · {fmtINR(filtered.reduce((a, o) => a + (o.total || 0), 0))}</div>
      </div>
      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line">
            <tr>{["Date", "Order #", "Customer", "Phone", "Items", "Payment Type", "Payment ID", "Credit Redeemed", "Amount"].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr>
          </thead>
          <tbody>
            {filtered.map((o) => (
              <tr key={o.id} className="border-b border-line/60" data-testid={`rev-order-${o.id}`}>
                <td className="p-3 text-xs">{new Date(o.paid_at || o.created_at).toLocaleString()}</td>
                <td className="p-3 text-xs">{o.id.slice(0, 8)}</td>
                <td className="p-3">{o.customer?.name}</td>
                <td className="p-3 text-ash text-xs">{o.customer?.phone || o.address?.phone || "—"}</td>
                <td className="p-3 text-xs">{(o.items || []).map((it) => `${it.name} ×${it.qty}`).join(", ")}</td>
                <td className="p-3 text-xs">{payType(o)}</td>
                <td className="p-3 text-xs text-ash">{o.razorpay_payment_id || "—"}</td>
                <td className="p-3 text-xs" data-testid={`rev-credit-${o.id}`}>
                  {o.credit_redeemed > 0
                    ? <span className="text-green-700 font-medium">−{fmtINR(o.credit_redeemed)}{o.credit_claim_type ? ` (${o.credit_claim_type})` : ""}</span>
                    : <span className="text-ash">—</span>}
                </td>
                <td className="p-3 font-medium text-maroon">{fmtINR(o.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && <div className="p-12 text-center text-ash text-sm">No paid orders match.</div>}
      </div>
    </div>
  );
}

// ============ RATE-LOCK TOKEN BOOKINGS (staff view) ============
export function LockRateBookingsPage() {
  const [items, setItems] = useState([]);
  const [tokenAmount, setTokenAmount] = useState("");
  const [settings, setSettings] = useState(null);
  const { user } = useAuth();
  const canEditToken = hasRole(user, ["super_admin", "major_admin", "admin"]);

  const load = () => api.get("/api/crm/lock-rate/bookings").then((r) => setItems(r.data || []));
  useEffect(() => {
    load();
    api.get("/api/site-settings").then((r) => {
      setSettings(r.data || {});
      setTokenAmount(r.data?.lock_rate_token_amount ?? 500);
    });
  }, []);

  const saveToken = async () => {
    try {
      await api.post("/api/crm/site-settings", { ...settings, lock_rate_token_amount: +tokenAmount || 500 });
      toast.success("Token amount updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="mb-6">
        <div className="eyebrow">Tokens</div>
        <h1 className="font-display text-4xl text-maroon">Rate-Lock Token Bookings</h1>
        <p className="text-xs text-ash mt-1">Every successful token payment made to lock the day's rate.</p>
      </div>

      {canEditToken && settings && (
        <div className="bg-white border border-line p-4 mb-6 max-w-md">
          <div className="eyebrow text-[10px] mb-2">Token amount to be collected (₹)</div>
          <div className="flex gap-2">
            <input type="number" className="border border-line px-3 py-2 bg-pearl text-sm flex-1" value={tokenAmount} onChange={(e) => setTokenAmount(e.target.value)} data-testid="token-amount-input"/>
            <button onClick={saveToken} className="btn-maroon text-sm" data-testid="save-token-amount">Save</button>
          </div>
        </div>
      )}

      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line">
            <tr>{["Name", "Phone", "Product", "Purity / Weight", "Locked ₹/g", "Transaction ID", "Txn Date", "Txn Time", "Amount Received", "Status"].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-b border-line/60" data-testid={`lock-booking-${b.id}`}>
                <td className="p-3">{b.customer_name}</td>
                <td className="p-3 text-xs">{b.customer_phone}</td>
                <td className="p-3 text-xs">{b.product_name || b.product_id?.slice(0, 8) || "—"}</td>
                <td className="p-3 text-xs">{b.desired_purity} · {b.desired_weight}g</td>
                <td className="p-3">{fmtINR(b.locked_rate_per_g)}</td>
                <td className="p-3 text-xs">{b.transaction_id || "—"}</td>
                <td className="p-3 text-xs">{b.paid_at ? new Date(b.paid_at).toLocaleDateString() : "—"}</td>
                <td className="p-3 text-xs">{b.paid_at ? new Date(b.paid_at).toLocaleTimeString() : "—"}</td>
                <td className="p-3 font-medium text-maroon">{b.payment_status === "paid" ? fmtINR(b.token_amount) : "—"}</td>
                <td className="p-3">
                  <span className={`px-2 py-0.5 text-[10px] uppercase tracking-widest ${b.payment_status === "paid" ? "bg-green-100 text-green-800" : b.payment_status === "failed" ? "bg-red-100 text-red-700" : "bg-gold/20 text-maroon"}`}>
                    {b.payment_status || b.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-12 text-center text-ash text-sm">No token bookings yet.</div>}
      </div>
    </div>
  );
}

// ============ STORES ADMIN (Super Admin only) ============
const emptyStore = { name: "", address: "", phone: "", hours: "10:00 AM – 9:00 PM", lat: "", lng: "", image_url: "", map_embed: "", directions_url: "" };

export function StoresAdminPage() {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyStore);
  const { user } = useAuth();

  const load = () => api.get("/api/crm/stores").then((r) => setItems(r.data || []));
  useEffect(() => { load(); }, []);

  if (user && !hasRole(user, "super_admin")) {
    return <div className="p-10 text-center text-ash">Only the Super Admin can manage stores.</div>;
  }

  const openCreate = () => { setEditingId(null); setForm(emptyStore); setShowForm(true); };
  const openEdit = (s) => { setEditingId(s.id); setForm({ ...emptyStore, ...s }); setShowForm(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, lat: form.lat === "" ? null : +form.lat, lng: form.lng === "" ? null : +form.lng };
    try {
      if (editingId) { await api.patch(`/api/crm/stores/${editingId}`, payload); toast.success("Store updated"); }
      else { await api.post("/api/crm/stores", payload); toast.success("Store added"); }
      setShowForm(false); setEditingId(null); setForm(emptyStore); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete store "${s.name}"?`)) return;
    try { await api.delete(`/api/crm/stores/${s.id}`); toast.success("Store deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 lg:p-8">
      <div className="flex items-center justify-between mb-6">
        <div><div className="eyebrow">Locations</div><h1 className="font-display text-4xl text-maroon">Stores</h1></div>
        <button onClick={openCreate} className="btn-maroon" data-testid="add-store-btn"><Plus size={14}/> Add Store</button>
      </div>
      <div className="bg-white border border-line overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-pearl border-b border-line">
            <tr>{["Image", "Name", "Address", "Phone", "Hours", "Actions"].map((h) => <th key={h} className="text-left p-3 eyebrow text-[10px]">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b border-line/60" data-testid={`store-row-${s.id}`}>
                <td className="p-3">
                  {s.image_url
                    ? <img src={s.image_url} alt="" className="w-14 h-14 object-cover border border-line"/>
                    : <div className="w-14 h-14 bg-pearl border border-line flex items-center justify-center text-[8px] text-ash">No image</div>}
                </td>
                <td className="p-3 font-medium">{s.name}</td>
                <td className="p-3 text-ash text-xs">{s.address}</td>
                <td className="p-3 text-xs">{s.phone}</td>
                <td className="p-3 text-xs">{s.hours}</td>
                <td className="p-3">
                  <div className="flex items-center gap-3">
                    <button onClick={() => openEdit(s)} className="inline-flex items-center gap-1 text-xs text-maroon hover:underline" data-testid={`edit-store-${s.id}`}><Pencil size={12}/> Edit</button>
                    <button onClick={() => remove(s)} className="inline-flex items-center gap-1 text-xs text-red-600 hover:underline" data-testid={`delete-store-${s.id}`}><Trash2 size={12}/> Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {items.length === 0 && <div className="p-12 text-center text-ash text-sm">No stores yet — add the first one.</div>}
      </div>

      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-6" onClick={() => setShowForm(false)}>
          <form onSubmit={submit} className="bg-white border border-line p-6 w-full max-w-lg space-y-3 max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <div className="font-display text-2xl text-maroon">{editingId ? "Edit store" : "Add store"}</div>
              <button type="button" onClick={() => setShowForm(false)}><X size={18}/></button>
            </div>
            <div>
              <div className="eyebrow text-[10px] mb-1">Storefront photo (shown on the public Stores page — hover reveals the details)</div>
              <ImageUploader
                value={form.image_url ? [form.image_url] : []}
                onChange={(urls) => setForm({...form, image_url: urls[0] || ""})}
                multiple={false} kind="store" maxFiles={1}
              />
            </div>
            <input required placeholder="Store name" className="w-full border border-line px-3 py-2 bg-pearl" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="store-name"/>
            <input placeholder="Address" className="w-full border border-line px-3 py-2 bg-pearl" value={form.address} onChange={(e) => setForm({...form, address: e.target.value})} data-testid="store-address"/>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Phone" className="w-full border border-line px-3 py-2 bg-pearl" value={form.phone} onChange={(e) => setForm({...form, phone: e.target.value})} data-testid="store-phone"/>
              <input placeholder="Hours" className="w-full border border-line px-3 py-2 bg-pearl" value={form.hours} onChange={(e) => setForm({...form, hours: e.target.value})} data-testid="store-hours"/>
              <input placeholder="Latitude (optional)" className="w-full border border-line px-3 py-2 bg-pearl" value={form.lat} onChange={(e) => setForm({...form, lat: e.target.value})} data-testid="store-lat"/>
              <input placeholder="Longitude (optional)" className="w-full border border-line px-3 py-2 bg-pearl" value={form.lng} onChange={(e) => setForm({...form, lng: e.target.value})} data-testid="store-lng"/>
            </div>
            <label className="block text-sm"><span className="eyebrow text-[10px]">Google Maps embed URL (Maps → Share → Embed a map → copy the src="...")</span>
              <textarea rows={2} className="w-full border border-line px-3 py-2 bg-pearl mt-1 text-xs" value={form.map_embed} onChange={(e) => setForm({...form, map_embed: e.target.value})} data-testid="store-map-embed"/>
            </label>
            <label className="block text-sm"><span className="eyebrow text-[10px]">"Navigate" button link (Maps → Share → copy link, or the dir/?api=1&destination=... URL)</span>
              <input className="w-full border border-line px-3 py-2 bg-pearl mt-1" value={form.directions_url} onChange={(e) => setForm({...form, directions_url: e.target.value})} data-testid="store-directions-url"/>
            </label>
            <button type="submit" className="btn-maroon w-full justify-center" data-testid="submit-store">{editingId ? "Save Changes" : "Add Store"}</button>
          </form>
        </div>
      )}
    </div>
  );
}
